import { SHARE_BANDS, SHARE_WINE_REF, quoteLength, GROUP_QUOTE_MAX } from "@proef/domain";
import { z } from "zod";
import { CardAudience, ReactionCounts, ReactionKind } from "./card.js";
import { PublicUser } from "./friendship.js";

/** What a share says of a line the person typed or picked: plain text, counted in characters. */
const ShareQuote = z.string().refine((text) => quoteLength(text) <= GROUP_QUOTE_MAX, {
  error: `at most ${String(GROUP_QUOTE_MAX)} characters`,
});

/**
 * The wine a share is about: a name and a year, and the catalogue id when the wine has a page every
 * viewer can open. Strict, so a share carries nothing about the wine that nobody asked for.
 */
export const SharedWine = z.strictObject({
  name: z.string().min(1).max(200),
  vintage: z.int().min(1800).max(2100).nullable(),
  wineId: z.string().regex(SHARE_WINE_REF).nullable(),
});
export type SharedWine = z.infer<typeof SharedWine>;

/**
 * PUT /v1/tastings/:tastingId/share -- the explicit act of sharing one tasting: the wine, a place
 * on the scale, who sees it and, only if the person wants, a line of their own. Strict by design:
 * there is no field a private note could travel in. The same tasting again replaces the share,
 * keeping its reactions.
 */
export const PutTastingShareRequest = z.strictObject({
  wine: SharedWine,
  band: z.enum(SHARE_BANDS),
  quote: ShareQuote.optional(),
  audience: CardAudience,
});
export type PutTastingShareRequest = z.infer<typeof PutTastingShareRequest>;

/**
 * A shared tasting as its viewer sees it. `audience` is set on the author's own share only, and
 * `reactions` are everyone's reactions to it.
 */
export const TastingShareView = z.object({
  id: z.string().min(1),
  user: PublicUser,
  wine: SharedWine,
  band: z.enum(SHARE_BANDS),
  quote: ShareQuote.min(1).exactOptional(),
  audience: CardAudience.nullable(),
  reactions: ReactionCounts,
  /** The viewer's own reaction; always null on their own share, which they cannot react to. */
  myReaction: ReactionKind.nullable(),
  sharedAt: z.iso.datetime(),
});
export type TastingShareView = z.infer<typeof TastingShareView>;

/** GET /v1/tasting-shares -- what friends and groups shared, newest first, cursor-paged. */
export const TastingShareList = z.object({
  items: z.array(TastingShareView),
  nextCursor: z.string().nullable(),
});
export type TastingShareList = z.infer<typeof TastingShareList>;
